import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Mail, Phone, Shield, CheckCircle, XCircle, AlertTriangle, Trash2, UserX } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";

interface Profile {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  user_type: string;
  email_verified: boolean;
  phone_verified: boolean;
  created_at: string;
  platform_source: string;
}

interface VerificationLog {
  id: string;
  profile: {
    full_name: string;
    email: string;
  };
  verification_type: string;
  action: string;
  success: boolean;
  created_at: string;
}

export default function AdminContactManagement() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [verificationLogs, setVerificationLogs] = useState<VerificationLog[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { isAdmin, isLoading: authLoading } = useAdminAuth();

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchData();
    }
  }, [authLoading, isAdmin]);

  // Realtime subscription to profile updates for verification status changes
  useEffect(() => {
    if (!isAdmin) return;

    const channel = supabase
      .channel('admin-verification-updates')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
      }, (payload) => {
        // Update the specific profile in the list
        setProfiles(prev => prev.map(profile => 
          profile.id === payload.new.id 
            ? { ...profile, ...payload.new }
            : profile
        ));
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin]);

  // Also refresh when a verification log is inserted (successful verify)
  useEffect(() => {
    if (!isAdmin) return;

    const logsChannel = supabase
      .channel('admin-verification-logs')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'verification_logs',
      }, (payload) => {
        if (payload.new?.action === 'verify' && payload.new?.success) {
          fetchData();
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(logsChannel);
    };
  }, [isAdmin]);

  const fetchData = async () => {
    if (!isAdmin) return;
    setIsLoading(true);

    // 1) Load user profiles first and always show them
    try {
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select(`
          id,
          full_name,
          email,
          phone,
          user_type,
          email_verified,
          phone_verified,
          created_at,
          platform_source
        `)
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;
      setProfiles(profilesData || []);
    } catch (error: any) {
      console.error("Error fetching profiles:", error);
      toast({
        title: "Error",
        description: "Failed to fetch user profiles.",
        variant: "destructive",
      });
    }

    // 2) Load verification logs, but do not block the profiles tab if restricted
    try {
      const { data: logsData, error: logsError } = await supabase
        .from("verification_logs")
        .select(`
          id,
          verification_type,
          action,
          success,
          created_at,
          profile_id
        `)
        .order("created_at", { ascending: false })
        .limit(100);

      if (logsError) throw logsError;

      const profileIds = [...new Set(logsData?.map((log) => log.profile_id) || [])];
      let logProfiles: Array<{ id: string; full_name: string; email: string }> = [];
      if (profileIds.length > 0) {
        const { data } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .in("id", profileIds);
        logProfiles = data || [];
      }

      const logsWithProfiles = (logsData || []).map((log) => ({
        ...log,
        profile:
          logProfiles.find((p) => p.id === log.profile_id) || {
            full_name: "Unknown User",
            email: "unknown@example.com",
          },
      }));

      setVerificationLogs(logsWithProfiles);
    } catch (error: any) {
      console.warn("Verification logs not available:", error?.message || error);
      setVerificationLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const manuallyVerify = async (profileId: string, verificationType: 'email' | 'phone') => {
    try {
      const verifiedField = verificationType === "phone" ? "phone_verified" : "email_verified";
      
      const { error } = await supabase
        .from("profiles")
        .update({ [verifiedField]: true })
        .eq("id", profileId);

      if (error) throw error;

      // Log admin action
      await supabase
        .from("verification_logs")
        .insert({
          profile_id: profileId,
          verification_type: verificationType,
          action: "admin_verify",
          success: true,
          notes: "Manually verified by admin"
        });

      toast({
        title: "Verification Updated",
        description: `${verificationType} has been manually verified.`,
      });

      fetchData();
    } catch (error: any) {
      console.error("Error updating verification:", error);
      toast({
        title: "Error",
        description: "Failed to update verification status.",
        variant: "destructive",
      });
    }
  };

  const resetVerification = async (profileId: string, verificationType: 'email' | 'phone') => {
    try {
      const verifiedField = verificationType === "phone" ? "phone_verified" : "email_verified";
      const codeField = verificationType === "phone" ? "phone_verification_code" : "email_verification_code";
      const expiresField = verificationType === "phone" ? "phone_verification_expires_at" : "email_verification_expires_at";
      const attemptsField = verificationType === "phone" ? "phone_verification_attempts" : "email_verification_attempts";
      
      const { error } = await supabase
        .from("profiles")
        .update({ 
          [verifiedField]: false,
          [codeField]: null,
          [expiresField]: null,
          [attemptsField]: 0
        })
        .eq("id", profileId);

      if (error) throw error;

      // Log admin action
      await supabase
        .from("verification_logs")
        .insert({
          profile_id: profileId,
          verification_type: verificationType,
          action: "admin_reset",
          success: true,
          notes: "Verification reset by admin"
        });

      toast({
        title: "Verification Reset",
        description: `${verificationType} verification has been reset.`,
      });

      fetchData();
    } catch (error: any) {
      console.error("Error resetting verification:", error);
      toast({
        title: "Error",
        description: "Failed to reset verification status.",
        variant: "destructive",
      });
    }
  };

  const deleteContact = async (profileId: string, profileName: string) => {
    if (!window.confirm(`Are you sure you want to delete contact for "${profileName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // Call secure edge function to delete the user and related data (admin only)
      const { data: fnData, error: fnError } = await supabase.functions.invoke('admin-delete-user', {
        body: { profileId }
      });

      if (fnError) {
        throw new Error(`Failed to delete user: ${fnError.message}`);
      }


      // Log admin action after successful deletion
      await supabase
        .from("verification_logs")
        .insert({
          profile_id: profileId,
          verification_type: "admin",
          action: "delete_contact",
          success: true,
          notes: `Contact and user account deleted by admin: ${profileName}`
        });

      toast({
        title: "Contact Deleted",
        description: `Contact and user account for "${profileName}" have been permanently deleted.`,
      });

      fetchData();
    } catch (error: any) {
      console.error("Error deleting contact:", error);
      toast({
        title: "Error",
        description: "Failed to delete contact. " + error.message,
        variant: "destructive",
      });
    }
  };

  const forceVerifyBoth = async (profileId: string) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ 
          email_verified: true,
          phone_verified: true 
        })
        .eq("id", profileId);

      if (error) throw error;

      // Log admin action
      await supabase
        .from("verification_logs")
        .insert({
          profile_id: profileId,
          verification_type: "both",
          action: "admin_verify_all",
          success: true,
          notes: "Both email and phone manually verified by admin"
        });

      toast({
        title: "Full Verification Applied",
        description: "Both email and phone have been verified.",
      });

      fetchData();
    } catch (error: any) {
      console.error("Error verifying both contacts:", error);
      toast({
        title: "Error",
        description: "Failed to verify both contacts.",
        variant: "destructive",
      });
    }
  };

  const filteredProfiles = profiles.filter(profile =>
    profile.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    profile.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    profile.phone.includes(searchTerm)
  );

  const getVerificationStats = () => {
    const totalUsers = profiles.length;
    const emailVerified = profiles.filter(p => p.email_verified).length;
    const phoneVerified = profiles.filter(p => p.phone_verified).length;
    const bothVerified = profiles.filter(p => p.email_verified && p.phone_verified).length;

    return { totalUsers, emailVerified, phoneVerified, bothVerified };
  };

  const stats = getVerificationStats();

  if (authLoading || isLoading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-destructive" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You don't have permission to access Contact Management. Admin privileges are required.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Contact Verification Management</h2>
        <Button onClick={fetchData} variant="outline">
          Refresh Data
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalUsers}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Email Verified</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.emailVerified}</div>
            <p className="text-xs text-muted-foreground">
              {((stats.emailVerified / stats.totalUsers) * 100).toFixed(1)}%
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Phone Verified</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.phoneVerified}</div>
            <p className="text-xs text-muted-foreground">
              {((stats.phoneVerified / stats.totalUsers) * 100).toFixed(1)}%
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Fully Verified</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{stats.bothVerified}</div>
            <p className="text-xs text-muted-foreground">
              {((stats.bothVerified / stats.totalUsers) * 100).toFixed(1)}%
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="profiles" className="space-y-4">
        <TabsList>
          <TabsTrigger value="profiles">User Profiles</TabsTrigger>
          <TabsTrigger value="logs">Verification Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="profiles" className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Contact Info</TableHead>
                    <TableHead>Email Status</TableHead>
                    <TableHead>Phone Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProfiles.map((profile) => (
                    <TableRow key={profile.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{profile.full_name || "Unnamed User"}</div>
                          <Badge variant="outline" className="text-xs">
                            {profile.platform_source === 'movable' 
                              ? (profile.user_type === 'tradesman' ? 'mover' : 'moving customer')
                              : profile.user_type}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-3 h-3" />
                            {profile.email}
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-3 h-3" />
                            {profile.phone === "PHONE_REQUIRED" ? "Not provided" : profile.phone}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {profile.email_verified ? (
                            <Badge variant="default" className="bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verified
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <XCircle className="w-3 h-3 mr-1" />
                              Unverified
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {profile.phone_verified ? (
                            <Badge variant="default" className="bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verified
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <XCircle className="w-3 h-3 mr-1" />
                              Unverified
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                       <TableCell>
                         <div className="flex gap-1 flex-wrap">
                           {/* Manual Verification Buttons */}
                           {!profile.email_verified && (
                             <Button
                               size="sm"
                               variant="outline"
                               onClick={() => manuallyVerify(profile.id, 'email')}
                               className="text-green-600 border-green-600 hover:bg-green-50"
                             >
                               <Mail className="w-3 h-3 mr-1" />
                               Verify Email
                             </Button>
                           )}
                           {!profile.phone_verified && profile.phone !== "PHONE_REQUIRED" && (
                             <Button
                               size="sm"
                               variant="outline"
                               onClick={() => manuallyVerify(profile.id, 'phone')}
                               className="text-green-600 border-green-600 hover:bg-green-50"
                             >
                               <Phone className="w-3 h-3 mr-1" />
                               Verify Phone
                             </Button>
                           )}
                           
                           {/* Quick verify both button */}
                           {(!profile.email_verified || !profile.phone_verified) && profile.phone !== "PHONE_REQUIRED" && (
                             <Button
                               size="sm"
                               variant="outline"
                               onClick={() => forceVerifyBoth(profile.id)}
                               className="text-blue-600 border-blue-600 hover:bg-blue-50"
                             >
                               <Shield className="w-3 h-3 mr-1" />
                               Verify All
                             </Button>
                           )}
                           
                           {/* Reset verification button */}
                           {(profile.email_verified || profile.phone_verified) && (
                             <Button
                               size="sm"
                               variant="outline"
                               onClick={() => profile.email_verified ? 
                                 resetVerification(profile.id, 'email') : 
                                 resetVerification(profile.id, 'phone')
                               }
                               className="text-orange-600 border-orange-600 hover:bg-orange-50"
                             >
                               <XCircle className="w-3 h-3 mr-1" />
                               Reset
                             </Button>
                           )}
                           
                           {/* Delete contact button */}
                           <Button
                             size="sm"
                             variant="outline"
                             onClick={() => deleteContact(profile.id, profile.full_name || profile.email)}
                             className="text-red-600 border-red-600 hover:bg-red-50"
                           >
                             <Trash2 className="w-3 h-3 mr-1" />
                             Delete
                           </Button>
                         </div>
                       </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Verification Activity</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {verificationLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{log.profile?.full_name || "Unknown"}</div>
                          <div className="text-sm text-muted-foreground">{log.profile?.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {log.verification_type === "email" ? (
                            <Mail className="w-3 h-3 mr-1" />
                          ) : (
                            <Phone className="w-3 h-3 mr-1" />
                          )}
                          {log.verification_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{log.action}</Badge>
                      </TableCell>
                      <TableCell>
                        {log.success ? (
                          <Badge variant="default" className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Success
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Failed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(log.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}